import { AlertTriangle, MessageCirclePlus, Paperclip, Trash2, X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChatComposer } from '@/components/chat/ChatComposer'
import { ChatMessageBubble } from '@/components/chat/ChatMessageBubble'
import { SparkyAnim, type SparkyAnimState } from '@/components/SparkyAnim'
import { SparkyThinking } from '@/components/SparkyThinking'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { amsterdamDay, formatDatumTijd } from '@/lib/format'
import { toast } from '@/lib/toast'
import { toastDeferredDelete } from '@/lib/toastUndo'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { cn } from '@/lib/utils'
import { useVisualViewportBox } from '@/lib/useVisualViewport'
import {
  createConversation,
  deleteAttachment,
  deleteConversation,
  getConversations,
  getMessages,
  listAttachments,
  patchConversation,
  postMessage,
  uploadAttachment,
} from './api'
import { type ComposerChip, ComposerChips, SentAttachments } from './AttachmentChips'
import { sparkyContextCopy } from './contextCopy'
import { ModelSwitch } from './ModelSwitch'
import { SaveToDealDialog, type SaveTarget } from './SaveToDealDialog'
import {
  SparkyApiError,
  type SparkyAttachment,
  type SparkyConversation,
  SparkyDisabledError,
  type SparkyMessage,
  type SparkyModelKey,
  SparkyRunActiveError,
} from './types'
import type { SparkyContext } from './useSparkyContext'
import { useSparkyRun } from './useSparkyRun'

/** From this width Sparky is a non-modal panel: no scrim, the page stays clickable. */
const WIDE_QUERY = '(min-width: 1440px)'

function conversationBadge(conversation: SparkyConversation): string {
  if (conversation.context_kind !== 'deal') return 'Dealbord'
  return conversation.context_deal_name ? `Deal: ${conversation.context_deal_name}` : 'Deal'
}

/** "<context> · <date time>": the line under a conversation title. */
function conversationMeta(conversation: SparkyConversation): string {
  const context =
    conversation.context_kind === 'deal'
      ? (conversation.context_deal_name ?? 'Deal')
      : 'Dealbord'
  return `${context} · ${formatDatumTijd(conversation.updated_at)}`
}

type ConversationGroupLabel = 'Vandaag' | 'Deze week' | 'Ouder'

/** Group the conversations (as the API returns them, newest first) by Amsterdam calendar day. */
function groupConversations(
  conversations: readonly SparkyConversation[],
  now: Date = new Date(),
): { label: ConversationGroupLabel; items: SparkyConversation[] }[] {
  const dayNumber = (day: string) => {
    const [year, month, date] = day.split('-').map(Number)
    return Date.UTC(year, month - 1, date) / 86_400_000
  }
  const today = dayNumber(amsterdamDay(now))
  const groups: { label: ConversationGroupLabel; items: SparkyConversation[] }[] = [
    { label: 'Vandaag', items: [] },
    { label: 'Deze week', items: [] },
    { label: 'Ouder', items: [] },
  ]
  for (const conversation of conversations) {
    const days = today - dayNumber(amsterdamDay(new Date(conversation.updated_at)))
    groups[days <= 0 ? 0 : days < 7 ? 1 : 2].items.push(conversation)
  }
  return groups.filter((group) => group.items.length > 0)
}

/** Mirrors the server limits (the server stays authoritative). */
const MAX_FILE_BYTES = 20 * 1024 * 1024
const MAX_FILES_PER_MESSAGE = 5
const ACCEPT = '.pdf,.pptx,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.gif'

const DISABLED_HINT = 'Sparky staat momenteel uit. Vraag een beheerder om de functie aan te zetten.'

/**
 * The global Sparky copilot slide-over (spec-29 §8): a right-anchored panel
 * with a conversation sidebar (new chat / open / delete) and the active chat.
 * Composes the shared Radix Dialog primitive into a sheet — same pattern as
 * the journey's `SparkySheet`, but wider (two columns) and feature-global.
 *
 * A conversation's context (deal/board) is snapshotted at creation: `context`
 * is only used when the FIRST message of a new chat is sent — the conversation
 * is created lazily right before that first turn posts (spec §2/§3). Opening
 * the sheet always starts from a fresh draft chat with the CURRENT page's
 * context; older chats stay reachable via the sidebar.
 */
export function SparkyCopilotSheet({
  open,
  onOpenChange,
  context,
  rememberedId = null,
  onRemember,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  context: SparkyContext
  /** The last conversation of this context (kept by `SparkyHost`, session only). */
  rememberedId?: string | null
  /** Called whenever the selected conversation of this context changes. */
  onRemember?: (conversationId: string | null) => void
}) {
  // spec-64 U-105: from 1440px Sparky sits next to the page instead of over it.
  const wide = useMediaQuery(WIDE_QUERY, false)
  const [conversations, setConversations] = useState<SparkyConversation[]>([])
  const [listError, setListError] = useState<string | null>(null)
  // null = a fresh draft chat against the current page context.
  const [selectedId, setSelectedIdState] = useState<string | null>(null)
  const setSelectedId = useCallback(
    (id: string | null) => {
      setSelectedIdState(id)
      onRemember?.(id)
    },
    [onRemember],
  )

  const [messages, setMessages] = useState<SparkyMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState<string | null>(null)

  const [content, setContent] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [sparkyDisabled, setSparkyDisabled] = useState(false)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)

  // Model of a draft chat (no conversation yet); sent on create.
  const [draftModel, setDraftModel] = useState<SparkyModelKey>('sonnet')
  const [chips, setChips] = useState<ComposerChip[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [saveTarget, setSaveTarget] = useState<SaveTarget | null>(null)
  const [savedNames, setSavedNames] = useState<Record<string, string>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  const chipCounter = useRef(0)
  // The draft chat's in-flight createConversation, so two quick drops share one conversation.
  const creatingConversation = useRef<Promise<string> | null>(null)
  useEffect(() => {
    if (selectedId === null) creatingConversation.current = null
  }, [selectedId])

  const reloadConversations = useCallback(async () => {
    try {
      setConversations(await getConversations())
      setListError(null)
    } catch (err) {
      // Flag off → the whole feature is asleep, not a list failure.
      if (err instanceof SparkyDisabledError) {
        setSparkyDisabled(true)
        setListError(null)
      } else {
        setListError('Kon de gesprekken niet laden.')
      }
    }
  }, [])

  // Bumped to re-pull the selected conversation's history (post-send, run end).
  const [messagesVersion, setMessagesVersion] = useState(0)
  const reloadMessages = useCallback(() => setMessagesVersion((v) => v + 1), [])

  // Every open starts from a fresh draft with the CURRENT context (spec §2);
  // the sidebar still lists older chats with their own frozen snapshots.
  // Ref-guarded so state only resets on the closed→open edge (no cascading
  // renders — same pattern as journey's DocumentWizard).
  const prevOpen = useRef(false)
  useEffect(() => {
    if (open && !prevOpen.current) {
      // Same context as the last chat → pick that conversation up again; the
      // load effect below fetches its messages. Another context starts empty.
      setSelectedIdState(rememberedId)
      setMessages([])
      setMessagesError(null)
      setSendError(null)
      setActiveRunId(null)
      setChips([])
      reloadMessages() // the same conversation may be selected again: re-pull its history
      void reloadConversations()
    }
    prevOpen.current = open
  }, [open, reloadConversations, reloadMessages, rememberedId])

  useEffect(() => {
    if (selectedId === null) return
    const conversationId = selectedId
    let cancelled = false
    async function load() {
      setMessagesLoading(true)
      try {
        const data = await getMessages(conversationId)
        if (!cancelled) {
          setMessages(data)
          setMessagesError(null)
        }
      } catch {
        if (!cancelled) setMessagesError('Kon de chat niet laden.')
      } finally {
        if (!cancelled) setMessagesLoading(false)
      }
    }
    // Pending uploads (not sent yet) live on the server, not only in this sheet:
    // bring them back as chips so they can be sent or removed (they count toward
    // the per-message and per-conversation caps).
    async function restorePending() {
      try {
        const rows = await listAttachments(conversationId)
        if (cancelled || !Array.isArray(rows)) return
        const pending = rows.filter((row) => row.message_id === null)
        if (pending.length === 0) return
        setChips((current) => {
          const known = new Set(
            current.flatMap((chip) => (chip.attachment ? [chip.attachment.id] : [])),
          )
          const restored: ComposerChip[] = pending
            .filter((row) => !known.has(row.id))
            .map((row) => ({
              key: `pending-${row.id}`,
              filename: row.filename,
              status: 'ready',
              attachment: row,
            }))
          return restored.length > 0 ? [...current, ...restored] : current
        })
      } catch {
        // Best effort: the composer still works without the restored chips.
      }
    }
    void load()
    void restorePending()
    return () => {
      cancelled = true
    }
  }, [selectedId, messagesVersion])

  // Short success/error pose right after a run ends, before settling back.
  const [flash, setFlash] = useState<'success' | 'error' | null>(null)
  const flashTimer = useRef<number | null>(null)
  useEffect(() => {
    return () => {
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current)
    }
  }, [])

  const running = activeRunId !== null
  const runState = useSparkyRun(selectedId, activeRunId, (status) => {
    setActiveRunId(null)
    reloadMessages()
    void reloadConversations()
    if (status === 'failed') {
      setSendError('Sparky kon geen antwoord geven. Probeer het opnieuw.')
    }
    setFlash(status === 'failed' ? 'error' : 'success')
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setFlash(null), 2200)
  })

  /** The open conversation; a draft chat gets its conversation (and context snapshot) now. */
  async function ensureConversation(): Promise<string> {
    if (selectedId !== null) return selectedId
    if (creatingConversation.current !== null) return creatingConversation.current
    const creating = createConversation({
      kind: context.kind,
      deal_id: context.dealId,
      route: context.route,
      ...(draftModel !== 'sonnet' ? { model_choice: draftModel } : {}),
    }).then(
      (conversation) => {
        setSelectedId(conversation.id)
        setConversations((current) => [conversation, ...current])
        return conversation.id
      },
      (err: unknown) => {
        creatingConversation.current = null
        throw err
      },
    )
    creatingConversation.current = creating
    return creating
  }

  const readyAttachmentIds = chips.flatMap((chip) =>
    chip.status === 'ready' && chip.attachment ? [chip.attachment.id] : [],
  )
  const uploading = chips.some((chip) => chip.status === 'uploading')

  async function send(override?: string) {
    const trimmed = (override ?? content).trim()
    if (!trimmed || running || sending || uploading || sparkyDisabled) return
    setSending(true)
    setSendError(null)
    try {
      // Lazy creation: the conversation (and its frozen context snapshot)
      // only comes into being with the first turn.
      const conversationId = await ensureConversation()
      const response =
        readyAttachmentIds.length > 0
          ? await postMessage(conversationId, trimmed, readyAttachmentIds)
          : await postMessage(conversationId, trimmed)
      if (override === undefined) setContent('')
      setChips([])
      reloadMessages()
      setActiveRunId(response.run_id)
    } catch (err) {
      if (err instanceof SparkyDisabledError) {
        setSparkyDisabled(true)
      } else if (err instanceof SparkyRunActiveError) {
        setSendError('Er loopt al een vraag. Wacht tot Sparky klaar is.')
      } else {
        setSendError('Versturen mislukt. Probeer het opnieuw.')
      }
    } finally {
      setSending(false)
    }
  }

  function changeModel(key: SparkyModelKey) {
    if (selectedConversation === null) {
      setDraftModel(key)
      return
    }
    const id = selectedConversation.id
    const previous = selectedConversation.model_choice ?? 'sonnet'
    const apply = (model: SparkyModelKey) =>
      setConversations((current) =>
        current.map((c) => (c.id === id ? { ...c, model_choice: model } : c)),
      )
    apply(key) // optimistic
    patchConversation(id, { model_choice: key }).catch(() => {
      apply(previous)
      toast.error('Model wisselen mislukt.')
    })
  }

  function uploadErrorText(err: unknown): string {
    return err instanceof SparkyApiError ? err.message : 'Uploaden mislukt. Probeer het opnieuw.'
  }

  async function addFiles(files: readonly File[]) {
    if (files.length === 0 || sparkyDisabled) return
    let ready = chips.filter((chip) => chip.status !== 'error').length
    const accepted: { key: string; file: File }[] = []
    const newChips: ComposerChip[] = []
    for (const file of files) {
      const key = `chip-${chipCounter.current++}`
      if (file.size > MAX_FILE_BYTES) {
        newChips.push({
          key,
          filename: file.name,
          status: 'error',
          error: 'Bestand is te groot (max 20 MB)',
        })
      } else if (ready >= MAX_FILES_PER_MESSAGE) {
        newChips.push({
          key,
          filename: file.name,
          status: 'error',
          error: 'Maximaal 5 bestanden per bericht',
        })
      } else {
        ready += 1
        accepted.push({ key, file })
        newChips.push({ key, filename: file.name, status: 'uploading' })
      }
    }
    setChips((current) => [...current, ...newChips])
    if (accepted.length === 0) return

    let conversationId: string
    try {
      conversationId = await ensureConversation()
    } catch (err) {
      if (err instanceof SparkyDisabledError) setSparkyDisabled(true)
      const keys = new Set(accepted.map((a) => a.key))
      setChips((current) =>
        current.map((chip) =>
          keys.has(chip.key)
            ? { ...chip, status: 'error', error: 'Uploaden mislukt. Probeer het opnieuw.' }
            : chip,
        ),
      )
      return
    }
    for (const { key, file } of accepted) {
      try {
        const attachment = await uploadAttachment(conversationId, file)
        setChips((current) =>
          current
            // A restore of the pending rows may already have shown this upload.
            .filter((chip) => chip.key === key || chip.attachment?.id !== attachment.id)
            .map((chip) =>
              chip.key === key ? { ...chip, status: 'ready', attachment } : chip,
            ),
        )
      } catch (err) {
        const error = uploadErrorText(err)
        setChips((current) =>
          current.map((chip) => (chip.key === key ? { ...chip, status: 'error', error } : chip)),
        )
      }
    }
  }

  function removeChip(chip: ComposerChip) {
    setChips((current) => current.filter((c) => c.key !== chip.key))
    if (chip.attachment && selectedId !== null) {
      deleteAttachment(selectedId, chip.attachment.id).catch(() => {
        toast.error('Bijlage verwijderen mislukt.')
      })
    }
  }

  function removeConversation(target: SparkyConversation) {
    const resetIfSelected = () => {
      if (selectedId === target.id) {
        setSelectedId(null)
        setMessages([])
        setMessagesError(null)
        setActiveRunId(null)
      }
    }
    toastDeferredDelete({
      message: 'Gesprek verwijderd.',
      hide: () => {
        setConversations((current) => current.filter((c) => c.id !== target.id))
        resetIfSelected()
      },
      restore: () => void reloadConversations(),
      commit: () => deleteConversation(target.id),
      error: 'Verwijderen mislukt.',
    })
  }

  const composerDisabled = running || sending || sparkyDisabled
  const viewportBox = useVisualViewportBox(open)

  // spec-65 U-042: an earlier conversation keeps the context it was started with.
  const selectedConversation =
    selectedId === null ? null : (conversations.find((c) => c.id === selectedId) ?? null)
  const modelValue: SparkyModelKey = selectedConversation
    ? (selectedConversation.model_choice ?? 'sonnet')
    : draftModel
  const copy = selectedConversation
    ? sparkyContextCopy(selectedConversation.context_kind, null)
    : sparkyContextCopy(context.kind, context.route)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={!wide}>
      <DialogPrimitive.Portal>
        {wide ? null : (
          <DialogPrimitive.Overlay
            className={cn(
              'vonk-dialog-overlay vonk-sparky-scrim',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            )}
          />
        )}
        <DialogPrimitive.Content
          data-slot="sparky-copilot-sheet"
          // Wide: a click on the page must not close the panel (Escape and the X still do).
          onInteractOutside={wide ? (event) => event.preventDefault() : undefined}
          onPointerDownOutside={wide ? (event) => event.preventDefault() : undefined}
          // The panel is fixed and full-height, with the composer at the bottom.
          // iOS Safari leaves the layout viewport at full height when the
          // keyboard opens, so without this the input sits behind the keyboard
          // and you type blind. `h-full` stays as the fallback where the visual
          // viewport is unavailable; where it is, top + height fully constrain
          // the box and `inset-y-0`'s bottom is ignored.
          style={
            viewportBox ? { top: viewportBox.offsetTop, height: viewportBox.height } : undefined
          }
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-3xl flex-col gap-4',
            'border-l border-border bg-background p-5 shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <DialogPrimitive.Title className="font-heading text-lg font-semibold text-foreground">
                  Vraag Sparky
                </DialogPrimitive.Title>
                <Badge variant="outline">Context: {copy.chip}</Badge>
              </div>
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                {copy.description}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              aria-label="Sluiten"
              className="rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <X className="size-5" aria-hidden />
            </DialogPrimitive.Close>
          </div>

          {/* Below md the sidebar moves above the chat: side by side it is w-56 of a
              375px screen, which leaves the conversation about 80px to live in. */}
          <div className="flex min-h-0 flex-1 gap-4 max-md:flex-col max-md:gap-2">
            {/* Conversation sidebar */}
            <aside className="flex w-56 shrink-0 flex-col gap-2 border-r border-border pr-3 max-md:w-full max-md:border-r-0 max-md:border-b max-md:pr-0 max-md:pb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedId(null)
                  setChips([])
                  setDraftModel('sonnet')
                  setMessages([])
                  setMessagesError(null)
                  setSendError(null)
                  setActiveRunId(null)
                }}
              >
                <MessageCirclePlus className="size-3.5" aria-hidden />
                Nieuwe chat
              </Button>
              {listError ? (
                <p role="alert" className="text-xs text-destructive">
                  {listError}
                </p>
              ) : null}
              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto max-md:max-h-28 max-md:flex-none">
                {groupConversations(conversations).map((group) => (
                  <section key={group.label} aria-label={group.label}>
                    <h3 className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </h3>
                    <ul className="flex flex-col gap-1">
                      {group.items.map((conversation) => (
                        <li key={conversation.id} className="group flex items-center gap-1">
                          <button
                            type="button"
                            className={cn(
                              'flex min-w-0 flex-1 flex-col gap-0.5 rounded-lg px-2 py-1.5 text-left transition-colors',
                              conversation.id === selectedId
                                ? 'bg-muted text-foreground'
                                : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                            )}
                            onClick={() => {
                              setSelectedId(conversation.id)
                              setChips([])
                              setSendError(null)
                              setActiveRunId(null)
                            }}
                          >
                            <span className="line-clamp-2 text-sm">
                              {conversation.title ?? 'Nieuw gesprek'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {conversationMeta(conversation)}
                            </span>
                            <Badge variant="outline" className="w-fit text-xs">
                              {conversationBadge(conversation)}
                            </Badge>
                          </button>
                          <button
                            type="button"
                            aria-label={`${conversation.title ?? 'Nieuw gesprek'} verwijderen`}
                            className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100 max-md:opacity-100"
                            onClick={() => removeConversation(conversation)}
                          >
                            <Trash2 className="size-3.5" aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </aside>

            {/* Active chat */}
            <section
              className={cn(
                'flex min-h-0 flex-1 flex-col gap-3 rounded-lg',
                dragOver && 'ring-2 ring-ring/50',
              )}
              onDragOver={(event) => {
                if (!event.dataTransfer.types.includes('Files')) return
                event.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                if (!event.dataTransfer.types.includes('Files')) return
                event.preventDefault()
                setDragOver(false)
                void addFiles(Array.from(event.dataTransfer.files))
              }}
            >
              <div className="min-h-0 flex-1 overflow-y-auto">
                {messagesLoading ? (
                  <p className="text-sm text-muted-foreground">Chat laden…</p>
                ) : messagesError ? (
                  <p role="alert" className="text-sm text-destructive">
                    {messagesError}
                  </p>
                ) : messages.length === 0 && !running ? (
                  <div className="flex w-full flex-col items-center gap-2 pt-4 text-center">
                    <SparkyAnim
                      state={
                        sparkyDisabled ? 'sleeping' : content.trim() ? 'asking' : 'greeting'
                      }
                      size={160}
                    />
                    <p className="font-heading text-base font-medium text-foreground">
                      {sparkyDisabled ? 'Sparky slaapt.' : 'Stel Sparky een vraag.'}
                    </p>
                    <p className="max-w-sm text-sm text-muted-foreground">
                      {sparkyDisabled
                        ? 'Hij wordt wakker zodra een beheerder de functie aanzet.'
                        : copy.sees}
                    </p>
                    {sparkyDisabled ? null : (
                      <div className="flex flex-wrap justify-center gap-2 pt-2">
                        {copy.suggestions.map((question) => (
                          <Button
                            key={question}
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={composerDisabled}
                            onClick={() => void send(question)}
                          >
                            {question}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {messages.map((message) => (
                      <SparkyMessageBubble
                        key={message.id}
                        message={message}
                        savedNames={savedNames}
                        onSaveAttachment={(attachment) =>
                          setSaveTarget({ kind: 'attachment', attachment })
                        }
                        onSaveAnswer={(m) => setSaveTarget({ kind: 'message', messageId: m.id })}
                      />
                    ))}
                  </ul>
                )}
                {running ? (
                  <div className="mt-3">
                    <SparkyThinking
                      caption={stepCaption(runState.currentStep)}
                      state={stepState(runState.currentStep)}
                    />
                  </div>
                ) : flash ? (
                  <div className="mt-3">
                    <SparkyThinking
                      caption={flash === 'success' ? 'Klaar!' : 'Dat ging mis.'}
                      state={flash}
                    />
                  </div>
                ) : null}
              </div>

              <ChatComposer
                value={content}
                onChange={setContent}
                onSend={() => void send()}
                disabled={composerDisabled}
                sendDisabled={content.trim().length === 0 || uploading}
                aboveSlot={
                  <div className="flex flex-col gap-2">
                    {sparkyDisabled ? null : (
                      <ModelSwitch value={modelValue} onChange={changeModel} />
                    )}
                    <ComposerChips chips={chips} onRemove={removeChip} />
                  </div>
                }
                leftAction={
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      hidden
                      accept={ACCEPT}
                      aria-label="Bestand kiezen"
                      onChange={(event) => {
                        void addFiles(Array.from(event.target.files ?? []))
                        event.target.value = ''
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="max-md:h-11"
                      disabled={composerDisabled}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="size-3.5" aria-hidden />
                      Bijlage
                    </Button>
                  </>
                }
                placeholder={copy.placeholder}
                error={sendError}
                hint={sparkyDisabled ? DISABLED_HINT : null}
              />
            </section>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
      <SaveToDealDialog
        open={saveTarget !== null}
        onOpenChange={(next) => {
          if (!next) setSaveTarget(null)
        }}
        target={saveTarget}
        defaultDealId={selectedConversation?.context_deal_id ?? context.dealId}
        onSaved={(target, startupName) => {
          if (target.kind === 'attachment') {
            setSavedNames((current) => ({ ...current, [target.attachment.id]: startupName }))
          }
        }}
      />
    </DialogPrimitive.Root>
  )
}

/** Dutch caption per run step (`loading_context` / `thinking` / `saving`). */
function stepCaption(step: string | null): string {
  switch (step) {
    case 'loading_context':
      return 'Sparky leest het dossier…'
    case 'saving':
      return 'Antwoord opslaan…'
    default:
      return 'Sparky denkt na…'
  }
}

/** Sparky animation state per run step. */
function stepState(step: string | null): SparkyAnimState {
  switch (step) {
    case 'loading_context':
      return 'analyzing'
    case 'saving':
      return 'writing'
    default:
      return 'thinking'
  }
}

function SparkyMessageBubble({
  message,
  savedNames,
  onSaveAttachment,
  onSaveAnswer,
}: {
  message: SparkyMessage
  savedNames: Readonly<Record<string, string>>
  onSaveAttachment: (attachment: SparkyAttachment) => void
  onSaveAnswer: (message: SparkyMessage) => void
}) {
  const isUser = message.role === 'user'
  const citations = message.citations ?? []
  const showFooter =
    !isUser &&
    (citations.length > 0 ||
      message.confidence_low === true ||
      Boolean(message.model_label) ||
      message.content !== '')
  return (
    <ChatMessageBubble
      isUser={isUser}
      markdown={!isUser}
      content={message.content}
      inside={
        isUser && (message.attachments ?? []).length > 0 ? (
          <SentAttachments
            attachments={message.attachments ?? []}
            savedNames={savedNames}
            onSave={onSaveAttachment}
          />
        ) : null
      }
      below={
        showFooter ? (
          <div className="flex w-full flex-col gap-1">
            {citations.length > 0 ? (
              <ul className="flex flex-wrap gap-1">
                {citations.map((citation) => (
                  <li key={citation.tag}>
                    <Badge variant="secondary" className="text-xs" title={citation.label}>
                      {citation.tag}
                      <span className="ml-1 max-w-40 truncate font-normal">{citation.label}</span>
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : null}
            {message.model_label || message.content !== '' ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {message.model_label ? <span>{message.model_label}</span> : null}
                {message.content !== '' ? (
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    onClick={() => onSaveAnswer(message)}
                  >
                    Antwoord opslaan bij deal
                  </button>
                ) : null}
              </div>
            ) : null}
            {message.confidence_low === true ? (
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <AlertTriangle className="size-3" aria-hidden />
                Sparky twijfelt over dit antwoord.
              </p>
            ) : null}
          </div>
        ) : null
      }
    />
  )
}
